import { FieldMeta } from '@tanstack/react-form';
import { JwtPayload } from 'jwt-decode';
import {
  DetailedHTMLProps,
  Dispatch,
  HTMLAttributes,
  ImgHTMLAttributes,
  SetStateAction,
  SVGProps as SVGProps_,
  TableHTMLAttributes,
} from 'react';
// @ts-expect-error cannot find module
import { JSONObject, JSONValue } from 'superjson/dist/types';

export type JSONType = JSONObject;

export type DivProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
export type SVGProps = SVGProps_<SVGSVGElement>;
export type TableProps = DetailedHTMLProps<TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>;
export type TableRowProps = DetailedHTMLProps<HTMLAttributes<HTMLTableRowElement>, HTMLTableRowElement>;
export type HrProps = DetailedHTMLProps<HTMLAttributes<HTMLHRElement>, HTMLHRElement>;
export type ImgProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export type OmitChildren<P = unknown> = Omit<P, 'children'>;

export const keys = <TObject extends object>(value: TObject) => {
  return Object.keys(value) as (keyof typeof value)[];
};

export const isNumber = (value: unknown, predicate?: (value: number) => boolean): value is number => {
  if (typeof value !== 'number') return false;
  if (isNaN(value)) return false;
  if (predicate) return predicate?.(value);
  return true;
};

export const isArray = (value: unknown) => {
  return Array.isArray(value);
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const isInArray = <TArrayData>(array: readonly TArrayData[], value: any): value is TArrayData => {
  return array.includes(value);
};

export const toString = (value?: JSONValue | null, defaultValue: string = ''): string => {
  if (value == null) return defaultValue;
  return value.toString();
};

export const toNumber = (value?: JSONValue | null, defaultValue: number = 0): number => {
  if (value == null) return defaultValue;
  const parsed = Number(value.toString());
  return isNaN(parsed) ? defaultValue : parsed;
};

export const toBoolean = (value?: JSONValue, defaultValue: boolean = false): boolean => {
  if (value == null) return defaultValue;
  return Boolean(value.toString());
};

export type Id = { id: string; hidden?: boolean };

export type FieldStateValueType = {
  confidence?: number;
  value?: string | null;
  rawValue?: string | null;
  isTouched?: boolean;
};
export type FieldStateArrayType = Record<string, FieldStateValueType[]>[];
export type FieldStateType = Record<string, FieldStateValueType[] | FieldStateArrayType>;

export type FieldContextType = {
  name: string;
  fields: FieldStateType;
  config: JSONType;
};

export type FormatterError = {
  id: string;
  name?: string | null;
  functionId?: string | null;
  config?: JSONType;
  isFormatter?: boolean;
  isValidator?: boolean;
  errors: string[];
};
export type ValidatorError = FormatterError;

export type FormatterWarning = {
  id: string;
  name?: string | null;
  functionId?: string | null;
  config?: JSONType;
  isFormatter?: boolean;
  isValidator?: boolean;
  warnings: string[];
};
export type ValidatorWarning = FormatterWarning;

export type FormatterWarningError = FormatterWarning & FormatterError;
export type ValidatorWarningError = ValidatorWarning & ValidatorError;

export type FormatterFnReturnType = {
  result?: FieldStateValueType;
  errors?: string[];
  warnings?: string[];
};

export type ValidatorFnReturnType = FormatterFnReturnType;

export type FormatterFnArgs = { event: FieldStateValueType; context: FieldContextType };
export type FormatterFnType = (args: FormatterFnArgs) => FormatterFnReturnType;
export type ValidatorFnArgs = { event: FieldStateValueType; context: FieldContextType };
export type ValidatorFnType = (args: FormatterFnArgs) => ValidatorFnReturnType;

export type SetState<TState> = Dispatch<SetStateAction<TState>>;

export type AttentionMap = number[][];

export type Annotation = {
  id?: string | null;
  rawValue?: string | null;
  confidence?: number | null;
  value?: string | null;
  attentionMap?: AttentionMap;
  page?: number | null;
  errors?: string[] | null;
  warnings?: string[] | null;
  formatters?: FormatterWarningError[];
  validators?: FormatterWarningError[];
};

export type HeaderAnnotations = Annotation[];
export type TableAnnotations = Record<string, Annotation[]>[];
export type Annotations = Record<string, HeaderAnnotations | TableAnnotations>;

export interface FieldMetaWithWarnings extends FieldMeta {
  warnings?: FormatterWarning[];
  errors?: FormatterError[];
}

export interface FieldMetaExtended extends FieldMeta {
  warnings?: FormatterWarning[];
  errors?: FormatterError[];
  formatters?: FormatterWarningError[];
  validators?: FormatterWarningError[];
  isReviewed?: boolean;
}

export type JWT = JwtPayload & { scope: string };
